import React, { useEffect, useState } from 'react';
import { User, Users, Globe } from 'lucide-react';
import { apiGet } from '../lib/api';

/**
 * ScopeToggle — 3-way Mine · My Team · All segmented control for hierarchical
 * record access (universal across Leads, Opportunities, Companies, etc.).
 *
 * Visibility rules:
 *  - super_admin / MD          → all 3 options
 *  - any other team manager    → Mine + My Team
 *  - non-managers              → component renders NOTHING (returns null)
 *
 * Props:
 *   value       'mine' | 'team' | 'all'
 *   onChange    fn(next)
 *   testid      base data-testid
 */
export default function ScopeToggle({ value, onChange, testid = 'scope-toggle' }) {
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    apiGet('/employees/am-i-manager').then(setMeta).catch(() => setMeta({ is_manager: false, can_see_all: false }));
  }, []);

  if (!meta) return null;
  if (!meta.is_manager) return null; // non-managers don't see the toggle

  const opts = [
    { v: 'mine', label: 'Mine', icon: User },
  ];
  // Anyone with a downstream team sees "My Team"
  if (meta.is_manager) opts.push({ v: 'team', label: 'My Team', icon: Users });
  if (meta.can_see_all) opts.push({ v: 'all', label: 'All', icon: Globe });

  return (
    <div
      className="inline-flex items-center gap-0.5 bg-slate-100 rounded-sm p-0.5 border border-slate-200"
      data-testid={testid}
    >
      {opts.map((o) => {
        const Icon = o.icon;
        const active = value === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            className={`flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-display font-bold uppercase tracking-wider rounded-sm transition-colors ${
              active ? 'bg-white text-[#1F7A6E] shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`${testid}-${o.v}`}
          >
            <Icon size={11} />
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
